import { useState } from "react";

const SUGGESTED_POSITION_CODES = [2, 3, 4, 5] as const;

export function ContactPositionLevelSelector({
  value,
  disabled,
  onChange,
  compact,
  hideHeader,
}: {
  value: number | null | undefined;
  disabled?: boolean;
  onChange: (code: number | null) => void;
  compact?: boolean;
  hideHeader?: boolean;
}) {
  const [customCode, setCustomCode] = useState("");

  const activeCode = value ?? null;
  const extraCodes =
    activeCode != null &&
    !(SUGGESTED_POSITION_CODES as readonly number[]).includes(activeCode)
      ? [activeCode]
      : [];

  const addCustom = () => {
    const code = Number.parseInt(customCode, 10);
    if (!Number.isFinite(code) || code < 2) return;
    onChange(code);
    setCustomCode("");
  };

  return (
    <div className="space-y-2">
      {!hideHeader ? (
        <>
          <p className="text-xs font-medium text-slate-600">Escalation level</p>
          {!compact ? (
            <p className="text-xs text-slate-500">
              Matches position codes in escalation settings. Use when your
              accounting system does not sync position levels.
            </p>
          ) : null}
        </>
      ) : null}
      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          disabled={disabled}
          onClick={() => onChange(null)}
          className={`rounded-lg border px-2.5 py-1 text-xs font-medium transition-colors disabled:opacity-50 ${
            activeCode === null
              ? "border-slate-400 bg-slate-100 text-slate-800"
              : "border-slate-200 bg-white text-slate-600 hover:border-slate-300"
          }`}
        >
          None
        </button>
        {SUGGESTED_POSITION_CODES.map((code) => {
          const active = activeCode === code;
          return (
            <button
              key={code}
              type="button"
              disabled={disabled}
              onClick={() => onChange(code)}
              className={`rounded-lg border px-2.5 py-1 text-xs font-medium transition-colors disabled:opacity-50 ${
                active
                  ? "border-[#4B7F73] bg-[#EEF4F2] text-[#4B7F73]"
                  : "border-slate-200 bg-white text-slate-600 hover:border-slate-300"
              }`}
            >
              {code}
            </button>
          );
        })}
        {extraCodes.map((code) => (
          <button
            key={code}
            type="button"
            disabled={disabled}
            onClick={() => onChange(code)}
            className="rounded-lg border border-[#4B7F73] bg-[#EEF4F2] px-2.5 py-1 text-xs font-medium text-[#4B7F73]"
          >
            Pos {code}
          </button>
        ))}
      </div>
      <div className="flex items-center gap-2">
        <input
          type="number"
          min={2}
          placeholder="Other"
          value={customCode}
          disabled={disabled}
          onChange={(e) => setCustomCode(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              addCustom();
            }
          }}
          className="w-20 rounded-md border border-slate-200 px-2 py-1 text-xs disabled:opacity-50"
        />
        <button
          type="button"
          disabled={disabled}
          onClick={addCustom}
          className="text-xs text-[#4B7F73] hover:underline disabled:opacity-50"
        >
          Add level
        </button>
      </div>
    </div>
  );
}
